import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  styled,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { CustomButton } from "@/components/custom/custom-button";
import { TagSection } from "./tag-section";
import { routeConfigSchema } from "@/schemas/routing.schema";
import { useDispatch } from "react-redux";
import { showToast } from "@/redux/actions/toast-actions";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(1),
    maxWidth: 520,
    width: "100%",
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: "10px",
  backgroundColor: theme.palette.primary.main + "18",
}));

const RouteConfigDialog = ({ open, onClose, data, mutationFn }) => {
  const queryClient = useQueryClient();

  const dispatch = useDispatch();

  const [initialState, setInitialState] = useState(null);
  const [routingTypes, setRoutingTypes] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);

  useEffect(() => {
    if (open && data) {
      const initial = {
        routingTypes: data.routingTypes ?? [],
        processors: data.processors ?? [],
        cardTypes: data.cardTypes ?? [],
      };

      setRoutingTypes(initial.routingTypes);
      setProcessors(initial.processors);
      setCardTypes(initial.cardTypes);
      setInitialState(initial);
    }
  }, [open, data]);

  const isDirty = (() => {
    if (!initialState) return false;

    return (
      JSON.stringify(initialState.routingTypes) !==
        JSON.stringify(routingTypes) ||
      JSON.stringify(initialState.processors) !== JSON.stringify(processors) ||
      JSON.stringify(initialState.cardTypes) !== JSON.stringify(cardTypes)
    );
  })();

  const mutation = useMutation({
    mutationFn,
    onSuccess: (data) => {
      dispatch(
        showToast(
          data?.message ?? "Configuration synced successfully",
          "success",
        ),
      );
      queryClient.invalidateQueries({ queryKey: ["route-config"] });
      onClose();
    },
    onError: (err) => {
      dispatch(
        showToast(err?.message ?? "Failed to sync configuration", "error"),
      );
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    mutation.reset();
    onClose();
  };

  const makeHandlers = (setter) => ({
    onRemove: (code) => setter((prev) => prev.filter((i) => i.code !== code)),

    onAdd: (item) =>
      setter((prev) =>
        prev.some((i) => i.code === item.code) ? prev : [...prev, item],
      ),
  });

  const handleSubmit = async () => {
    const payload = { routingTypes, processors, cardTypes };

    try {
      await routeConfigSchema.validate(payload, { abortEarly: false });
      mutation.mutate(payload);
    } catch (err) {
      console.error("Validation errors:", err);
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          pt: 2,
          px: 2,
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconWrapper>
            <TuneIcon sx={{ color: "primary.main", fontSize: 22 }} />
          </IconWrapper>

          <Box>
            <DialogTitle
              sx={{
                p: 0,
                fontSize: "1rem",
                fontWeight: 600,
              }}
            >
              Edit Routing Configuration
            </DialogTitle>

            <Typography variant="caption" color="text.secondary">
              Manage routing types, processors and card types.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TagSection
            label="Routing Types"
            items={routingTypes}
            {...makeHandlers(setRoutingTypes)}
          />

          <Divider />

          <TagSection
            label="Processors"
            items={processors}
            {...makeHandlers(setProcessors)}
          />

          <Divider />

          <TagSection
            label="Card Types"
            items={cardTypes}
            {...makeHandlers(setCardTypes)}
          />
        </Box>

        {mutation.isError && (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {mutation.error?.message ?? "Failed to save. Try again."}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 2, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
        >
          Cancel
        </CustomButton>

        <CustomButton
          size="small"
          onClick={handleSubmit}
          disabled={mutation.isPending || !isDirty}
          sx={{ minWidth: 120 }}
        >
          {mutation.isPending ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={14} sx={{ color: "#fff" }} />
              Saving...
            </Box>
          ) : (
            "Save Changes"
          )}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default RouteConfigDialog;
